import React from 'react';
import { CustomModal } from '../../../../common/CustomModal';
import {
  User,
  Phone,
  Mail,
  IdCard,
  Building2,
  FileText,
  Image as ImageIcon,
  Layers,
  Wallet,
  Calendar,
  Banknote,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Download,
  Maximize2,
  X,
  Clock,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Upload,
  AlertCircle,
  HelpCircle,
  Settings2,
  Landmark,
  CreditCard,
  UserRound,
  Receipt,
  MinusCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import '../../../../../styles/components/enrollment-list-details-modal.scss';
import { StatusNewBadge } from '../../../../common/StatusBadge';
import {
  ENROLLMENT_REFUND_STATUS,
  PAYMENT_ACCOUNT_TYPE,
  STRATEGY_TYPE,
  TRANSACTION_METHODS,
} from '../../../../../constants/status';
import i18n from '../../../../../i18n';
import { formatDate, formatAmount, getActorLabel } from '../../../../../utils/common';
import { imageUrl } from '../../../../../constants/global';
import { refundAction } from '../services/refundService';
// NOTE: swap this for your real enrollment approve/reject endpoint

const ROLE_GENERAL_MANAGER = 'GM';
const ROLE_PROJECT_MANAGER = 'PM';
const ROLE_FINANCE = 'F';
const ROLE_CLIENT = 'C';

const REFUND_PENDING_CLIENT = 'WC';
const REFUND_PENDING_MZADCOM = 'A';

const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const DEDUCTION_CUSTOM = 'custom';

const DEDUCTION_REASONS = [
  {
    key: 'refuse_material',
    i18nKey: 'penalty_refuse_material',
    label: 'Penalty for refusing to take material',
  },
  {
    key: 'payment_not_done',
    i18nKey: 'penalty_payment_not_done',
    label: 'Penalty for payment not done',
  },
  {
    key: 'site_not_cleared',
    i18nKey: 'penalty_site_not_cleared',
    label: 'Penalty for site not cleared',
  },
  { key: DEDUCTION_CUSTOM, i18nKey: 'custom_reason', label: 'Custom reason' },
];

export class DetailsModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      submitting: false,
      showRejectInput: false,
      rejectReason: '',
      rejectError: '',

      approvalDoc: null,
      approvalDocPreviewUrl: null,
      dragOver: false,
      fileError: '',

      receiptZoom: 1,
      receiptRotation: 0,
      showFullscreenReceipt: false,

      docView: {},

      showDeductInput: false,
      deductionType: '',
      deductionCustomReason: '',
      deductionError: '',
    };

    this.rejectReasonRef = React.createRef();
    this.approveDocRef = React.createRef();
    this.deductReasonRef = React.createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    const { show, selectedTransaction } = this.props;
    const justOpened = show && !prevProps.show;
    const transactionChanged = selectedTransaction?.id !== prevProps.selectedTransaction?.id;

    if (justOpened || (show && transactionChanged)) {
      this.resetState();
    }

    if (this.state.showRejectInput && !prevState.showRejectInput) {
      this.rejectReasonRef.current?.focus();
    }

    if (
      this.state.deductionType === DEDUCTION_CUSTOM &&
      prevState.deductionType !== DEDUCTION_CUSTOM
    ) {
      this.deductReasonRef.current?.focus();
    }
  }

  componentWillUnmount() {
    this.revokePreview();
  }

  revokePreview = () => {
    if (this.state.approvalDocPreviewUrl) {
      URL.revokeObjectURL(this.state.approvalDocPreviewUrl);
    }
  };

  resetState = () => {
    this.revokePreview();
    this.setState({
      submitting: false,
      showRejectInput: false,
      rejectReason: '',
      rejectError: '',
      approvalDoc: null,
      approvalDocPreviewUrl: null,
      dragOver: false,
      fileError: '',
      receiptZoom: 1,
      receiptRotation: 0,
      showFullscreenReceipt: false,
      approvalDocZoom: 1,
      approvalDocRotation: 0,
      showFullscreenApprovalDoc: false,
      strategyEnabled: Boolean(this.props.selectedTransaction?.strategy_enabled),
      showDeductInput: false,
      deductionType: '',
      deductionCustomReason: '',
      deductionError: '',
      docView: {},
    });
  };

  handleClose = () => {
    this.resetState();
    this.props.onClose();
  };

  // ---------- derived flags ----------
  get canDeduct() {
    return this.canAct && this.refundStatus === REFUND_PENDING_MZADCOM;
  }

  get refundStatus() {
    return this.props.selectedTransaction?.refund_status;
  }

  get isClient() {
    return this.props.role === ROLE_CLIENT;
  }
  get isClientStage() {
    return this.refundStatus === REFUND_PENDING_CLIENT;
  }
  // Only the client attaches an approval document
  get requireAttachment() {
    return this.isClientStage && this.canAct;
  }

  get canAct() {
    const { role } = this.props;
    const status = this.refundStatus;

    if (this.isClientStage) {
      return [ROLE_CLIENT, ROLE_FINANCE].includes(role);
    }
    if (status === REFUND_PENDING_MZADCOM) {
      return [ROLE_PROJECT_MANAGER, ROLE_GENERAL_MANAGER].includes(role);
    }
    return false; // PA / RR / RC are terminal
  }

  // ---------- file handling (approval document) ----------

  validateFile = (file) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return i18n.t('invalid_file_type') || 'Only JPG, PNG, PDF or Word files are allowed';
    }
    if (file.size > MAX_FILE_SIZE) {
      return i18n.t('file_too_large') || 'File size must not exceed 5 MB';
    }
    return '';
  };

  setApprovalDoc = (file) => {
    if (!file) return;
    const error = this.validateFile(file);
    if (error) {
      this.setState({ fileError: error });
      return;
    }
    this.revokePreview();
    const isImage = file.type.startsWith('image/');
    this.setState({
      approvalDoc: file,
      approvalDocPreviewUrl: isImage ? URL.createObjectURL(file) : null,
      fileError: '',
    });
  };

  handleFileInputChange = (e) => {
    const file = e.target.files?.[0] || null;
    this.setApprovalDoc(file);
    e.target.value = '';
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.state.submitting) return;
    this.setState({ dragOver: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragOver: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragOver: false });
    if (this.state.submitting) return;
    this.setApprovalDoc(e.dataTransfer.files?.[0] || null);
  };

  removeApprovalDoc = () => {
    this.revokePreview();
    this.setState({ approvalDoc: null, approvalDocPreviewUrl: null, fileError: '' });
  };

  handleStrategyToggle = () => {
    this.setState((s) => ({ strategyEnabled: !s.strategyEnabled }));
  };

  // ---------- receipt preview controls ----------

  zoomIn = () => this.setState((s) => ({ receiptZoom: Math.min(s.receiptZoom + 0.25, 3) }));
  zoomOut = () => this.setState((s) => ({ receiptZoom: Math.max(s.receiptZoom - 0.25, 0.5) }));
  rotateReceipt = () => this.setState((s) => ({ receiptRotation: (s.receiptRotation + 90) % 360 }));
  toggleFullscreenReceipt = () =>
    this.setState((s) => ({ showFullscreenReceipt: !s.showFullscreenReceipt }));

  downloadReceipt = () => {
    const url = this.getReceiptUrl();
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = '';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  getReceiptUrl = () => {
    const { selectedTransaction } = this.props;
    if (!selectedTransaction?.file_receipt) return null;
    return `${imageUrl}uploads/enrolls/receipt/${selectedTransaction.file_receipt}`;
  };

  // ---------- approval document preview controls ----------

  getDocView = (key) => this.state.docView[key] || { zoom: 1, rotation: 0, fullscreen: false };

  setDocView = (key, patch) =>
    this.setState((s) => ({
      docView: {
        ...s.docView,
        [key]: { ...this.getDocView(key), ...patch },
      },
    }));

  docZoomIn = (key) =>
    this.setDocView(key, { zoom: Math.min(this.getDocView(key).zoom + 0.25, 3) });

  docZoomOut = (key) =>
    this.setDocView(key, { zoom: Math.max(this.getDocView(key).zoom - 0.25, 0.5) });

  docRotate = (key) =>
    this.setDocView(key, { rotation: (this.getDocView(key).rotation + 90) % 360 });

  docToggleFullscreen = (key) =>
    this.setDocView(key, { fullscreen: !this.getDocView(key).fullscreen });

  docUrl = (fileName) => (fileName ? `${imageUrl}uploads/enrolls/receipt/${fileName}` : null);

  downloadDoc = (fileName) => {
    const url = this.docUrl(fileName);
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = '';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  // ---------- actions ----------

  handleApprove = async () => {
    const { selectedTransaction, onComplete, role } = this.props;

    if (this.requireAttachment && !this.state.approvalDoc) {
      this.setState({
        fileError: i18n.t('approval_document_required') || 'Please attach a document to approve',
      });
      this.approveDocRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      this.setState({ submitting: true, fileError: '' });

      const payload = new FormData();
      payload.append('id', selectedTransaction.id);
      payload.append('action', 'approve');
      payload.append('role', role);

      if (this.requireAttachment && this.state.approvalDoc) {
        payload.append('refund_document', this.state.approvalDoc);
      }

      const response = await refundAction(payload);

      if (response?.success) {
        toast.success(
          response.message || i18n.t('refund_approved_successfully') || 'Refund  approved'
        );
        onComplete?.();
        this.handleClose();
      } else {
        toast.error(response?.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      toast.error(error?.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleRejectClick = () => this.setState({ showRejectInput: true });

  handleBackFromReject = () =>
    this.setState({ showRejectInput: false, rejectReason: '', rejectError: '' });

  handleReject = async () => {
    const { selectedTransaction, onComplete, role } = this.props;
    const { rejectReason } = this.state;

    if (!rejectReason.trim()) {
      this.setState({
        rejectError: i18n.t('reject_reason_is_required') || 'Reject reason is required',
      });
      this.rejectReasonRef.current?.focus();
      return;
    }

    try {
      this.setState({ submitting: true, rejectError: '' });

      const response = await refundAction({
        id: selectedTransaction.id,
        action: 'reject',
        role,
        reason: rejectReason.trim(),
      });

      if (response?.success) {
        toast.success(
          response.message || i18n.t('refund_rejected_successfully') || 'Refund  rejected'
        );
        onComplete?.();
        this.handleClose();
      } else {
        toast.error(response?.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      toast.error(error?.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ submitting: false });
    }
  };

  // ---------- deduction ----------

  handleDeductClick = () => {
    if (!this.canDeduct) return;
    this.setState({ showDeductInput: true });
  };

  handleBackFromDeduct = () =>
    this.setState({
      showDeductInput: false,
      deductionType: '',
      deductionCustomReason: '',
      deductionError: '',
    });

  handleDeductionTypeChange = (e) =>
    this.setState({ deductionType: e.target.value, deductionError: '' });

  getDeductionReasonText = () => {
    const { deductionType, deductionCustomReason } = this.state;
    if (!deductionType) return '';
    if (deductionType === DEDUCTION_CUSTOM) return deductionCustomReason.trim();
    const preset = DEDUCTION_REASONS.find((r) => r.key === deductionType);
    return preset ? i18n.t(preset.i18nKey) || preset.label : '';
  };

  handleDeduct = async () => {
    if (!this.canDeduct) return;
    const { selectedTransaction, onComplete, role } = this.props;
    const { deductionType } = this.state;

    if (!deductionType) {
      this.setState({
        deductionError: i18n.t('select_deduction_reason') || 'Please select a deduction reason',
      });
      return;
    }

    const reason = this.getDeductionReasonText();
    if (!reason) {
      this.setState({
        deductionError: i18n.t('deduction_reason_required') || 'Please enter the deduction reason',
      });
      this.deductReasonRef.current?.focus();
      return;
    }

    try {
      this.setState({ submitting: true, deductionError: '' });

      const response = await refundAction({
        id: selectedTransaction.id,
        role,
        action: 'deduct',
        reason,
      });

      if (response?.success) {
        toast.success(
          response.message || i18n.t('deduction_applied_successfully') || 'Deduction applied'
        );
        onComplete?.();
        this.handleClose();
      } else {
        toast.error(response?.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      toast.error(error?.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ submitting: false });
    }
  };

  // ---------- timeline ----------

  getInitials = (name) =>
    (name || '')
      .trim()
      .split(/\s+/)
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '--';

  buildTimeline = () => {
    const t = this.props.selectedTransaction;
    if (!t) return [];

    const clientLabel = i18n.t('client_manager');
    const entries = [];

    entries.push({
      key: 'submitted',
      actor: t.user?.name || '--',
      date: t.created_at,
      title: i18n.t('enrollment_submitted') || 'Enrollment Submitted',
      description:
        i18n.t('enrollment_submitted_desc') || 'Bidder submitted the enrollment request.',
      tone: 'neutral',
    });

    // ── Enrollment phase ────────────────────────────────

    if (t.client_approved_at) {
      entries.push({
        key: 'client_approved',
        actor: getActorLabel(t.client_approved_by, clientLabel),
        date: t.client_approved_at,
        title: i18n.t('enrollment_client_approved') || 'Enrollment Approved by Client',
        description:
          i18n.t('enrollment_client_approved_desc') ||
          'Client verified the payment and approved the enrollment.',
        document: t.client_approval_doc,
        tone: 'approved',
      });
    }

    if (t.client_rejected_at) {
      entries.push({
        key: 'client_rejected',
        actor: getActorLabel(t.client_rejected_by, clientLabel),
        date: t.client_rejected_at,
        title: i18n.t('enrollment_client_rejected') || 'Enrollment Rejected by Client',
        description: t.client_reject_reason || '--',
        tone: 'rejected',
      });
    }

    if (t.mzadcom_approved_at) {
      entries.push({
        key: 'mzadcom_approved',
        actor: getActorLabel(t.mzadcom_approved_by, 'Mzadcom'),
        date: t.mzadcom_approved_at,
        title: i18n.t('enrollment_mzadcom_approved') || 'Enrollment Approved by Mzadcom',
        description:
          i18n.t('enrollment_mzadcom_approved_desc') ||
          'Mzadcom completed the final enrollment review.',
        tone: 'approved',
      });
    }

    if (t.mzadcom_rejected_at) {
      entries.push({
        key: 'mzadcom_rejected',
        actor: getActorLabel(t.mzadcom_rejected_by, 'Mzadcom'),
        date: t.mzadcom_rejected_at,
        title: i18n.t('enrollment_mzadcom_rejected') || 'Enrollment Rejected by Mzadcom',
        description: t.mzadcom_reject_reason || '--',
        tone: 'rejected',
      });
    }

    // ── Refund phase ────────────────────────────────────

    if (t.refund_mzadcom_approved_at) {
      entries.push({
        key: 'refund_mzadcom_approved',
        actor: getActorLabel(t.refund_mzadcom_approved_by, 'Mzadcom'),
        date: t.refund_mzadcom_approved_at,
        title: i18n.t('refund_mzadcom_approved') || 'Refund Approved by Mzadcom',
        description:
          i18n.t('refund_mzadcom_approved_desc') || 'Mzadcom reviewed and approved the refund.',
        tone: 'approved',
      });
    }

    if (t.refund_mzadcom_rejected_at) {
      entries.push({
        key: 'refund_mzadcom_rejected',
        actor: getActorLabel(t.refund_mzadcom_rejected_by, 'Mzadcom'),
        date: t.refund_mzadcom_rejected_at,
        title: i18n.t('refund_mzadcom_rejected') || 'Refund Rejected by Mzadcom',
        description: t.refund_mzadcom_reject_reason || '--',
        tone: 'rejected',
      });
    }

    if (t.refund_client_approved_at) {
      entries.push({
        key: 'refund_client_approved',
        actor: getActorLabel(t.refund_client_approved_by, clientLabel),
        date: t.refund_client_approved_at,
        title: i18n.t('refund_client_approved') || 'Refund Approved by Client',
        description:
          i18n.t('refund_client_approved_desc') ||
          'Client confirmed the refund and attached supporting documentation.',
        document: t.refund_client_doc,
        tone: 'approved',
      });
    }

    if (t.refund_client_rejected_at) {
      entries.push({
        key: 'refund_client_rejected',
        actor: getActorLabel(t.refund_client_rejected_by, clientLabel),
        date: t.refund_client_rejected_at,
        title: i18n.t('refund_client_rejected') || 'Refund Rejected by Client',
        description: t.refund_client_reject_reason || '--',
        tone: 'rejected',
      });
    }

    if (t.refund_deducted_at) {
      entries.push({
        key: 'refund_penalty',
        actor: getActorLabel(t.refund_deducted_by, clientLabel),
        date: t.refund_deducted_at,
        title: i18n.t('enrollment_penalty_deduction'),
        description: t.refund_deduction_reason || '--',
        tone: 'rejected',
      });
    }

    return entries.filter((e) => e.date).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // ---------- render helpers ----------

  renderBidderDetails() {
    const { user } = this.props.selectedTransaction;
    const civilId = user?.resident_card_number || user?.resident_card_number || '--';

    return (
      <section className="dm-section">
        <h6 className="dm-section-title">
          <User size={16} strokeWidth={1.75} /> {i18n.t('bidder_details') || 'Bidder Details'}
        </h6>
        <div className="dm-info-grid">
          <div className="dm-info-item">
            <span className="dm-info-label">
              <Phone size={12} /> {i18n.t('mobile') || 'Mobile'}
            </span>
            <span className="dm-info-value">
              {user?.country_code || ''} {user?.mobile || '--'}
            </span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">
              <Mail size={12} /> {i18n.t('email') || 'Email'}
            </span>
            <span className="dm-info-value">{user?.email || '--'}</span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">
              <IdCard size={12} /> {i18n.t('civil_id') || 'Civil ID'}
            </span>
            <span className="dm-info-value dm-info-value--with-action">
              {civilId}
              {user?.file_id_number && (
                <a
                  className="dm-inline-link"
                  href={`${imageUrl}uploads/users/id/${user.file_id_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {i18n.t('view') || 'View'}
                </a>
              )}
            </span>
          </div>
          {/* <div className="dm-info-item">
            <span className="dm-info-label">
              <Building2 size={12} /> {i18n.t('company') || 'Company'}
            </span>
            <span className="dm-info-value">{companyName}</span>
          </div> */}
          <div className="dm-info-item">
            <span className="dm-info-label">
              <User size={12} /> {i18n.t('username') || 'Username'}
            </span>
            <span className="dm-info-value">{user?.username || '--'}</span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">
              <ImageIcon size={12} /> {i18n.t('signature') || 'Signature'}
            </span>
            <span className="dm-info-value">
              {user?.signature ? (
                <a
                  className="dm-inline-link"
                  href={`${imageUrl}uploads/users/signature/${user.signature}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {i18n.t('view_signature') || 'View Signature'}
                </a>
              ) : (
                '--'
              )}
            </span>
          </div>
        </div>
      </section>
    );
  }

  renderAuctionAccess() {
    const { group, auction, auctions } = this.props.selectedTransaction;
    const isGrouped = group?.is_grouped_enroll;

    return (
      <section className="dm-section">
        <h6 className="dm-section-title">
          <Layers size={16} strokeWidth={1.75} /> {i18n.t('auction_access') || 'Auction Access'}
        </h6>
        {isGrouped ? (
          <span className="dm-badge dm-badge--muted">
            <Layers size={13} /> {i18n.t('all_auctions') || 'All Auctions'} ·{' '}
            {auctions?.length || 0}
          </span>
        ) : (
          <span className="dm-badge dm-badge--muted">{auction?.title || '--'}</span>
        )}
      </section>
    );
  }

  renderPaymentVerification() {
    const t = this.props.selectedTransaction;
    const { group } = t;
    const companyName = group?.organization_details?.organization_name || '--';
    const isArabic = i18n.language === 'ar';

    return (
      <section className="dm-section">
        <h6 className="dm-section-title">
          <Wallet size={16} strokeWidth={1.75} />{' '}
          {i18n.t('payment_verification') || 'Payment Verification'}
        </h6>
        <div className="dm-info-grid">
          <div className="dm-info-item">
            <span className="dm-info-label">{i18n.t('type') || 'Type'}</span>
            <span className="dm-info-value">
              <StatusNewBadge status={TRANSACTION_METHODS[t.pay_type]} />
            </span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">{i18n.t('account') || 'Account'}</span>
            <span className="dm-info-value">
              <StatusNewBadge status={PAYMENT_ACCOUNT_TYPE[t.payment_account_type_id]} />
            </span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">
              <Landmark size={12} /> {i18n.t('bank') || 'Company'}
            </span>
            <span className="dm-info-value">{t.bank}</span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">
              <CreditCard size={12} /> {i18n.t('account_number') || 'Company'}
            </span>
            <span className="dm-info-value">{t.account_number}</span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">
              <UserRound size={12} /> {i18n.t('beneficiary') || 'Company'}
            </span>
            <span className="dm-info-value">{t.beneficiary}</span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">
              <Receipt size={12} /> {i18n.t('receipt_number') || 'Company'}
            </span>
            <span className="dm-info-value">{t.receipt_number}</span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">
              <Calendar size={12} /> {i18n.t('payment_date') || 'Date'}
            </span>
            <span className="dm-info-value">
              {t.transfer_date ? formatDate(t.transfer_date) : '--'}
            </span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">
              <Banknote size={12} /> {i18n.t('amount') || 'Amount'}
            </span>
            <span className="dm-info-value dm-info-value--amount">
              {formatAmount(t.amount, isArabic ? 'ar-OM-u-nu-arab' : 'en-US')}
            </span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">
              <Building2 size={12} /> {i18n.t('company') || 'Company'}
            </span>
            <span className="dm-info-value">{companyName}</span>
          </div>
          <div className="dm-info-item">
            <span className="dm-info-label">{i18n.t('strategy') || 'Strategy'}</span>
            <span className="dm-info-value">
              <StatusNewBadge status={STRATEGY_TYPE[t.strategy_enabled]} />
            </span>
          </div>
        </div>

        {this.renderReceiptPreview()}
      </section>
    );
  }

  renderReceiptPreview() {
    const { receiptZoom, receiptRotation } = this.state;
    const receiptUrl = this.getReceiptUrl();
    const isPdf = /\.pdf$/i.test(this.props.selectedTransaction?.file_receipt || '');

    return (
      <div className="dm-receipt-box">
        <div className="dm-receipt-canvas">
          {!receiptUrl ? (
            <div className="dm-receipt-placeholder">
              {i18n.t('receipt_preview') || 'Receipt Preview'}
            </div>
          ) : isPdf ? (
            <div className="dm-receipt-pdf">
              <FileText size={32} />
              <span>{this.props.selectedTransaction.file_receipt}</span>
            </div>
          ) : (
            <img
              src={receiptUrl}
              alt="Receipt"
              style={{ transform: `scale(${receiptZoom}) rotate(${receiptRotation}deg)` }}
            />
          )}
        </div>
        <div className="dm-receipt-controls">
          <button
            type="button"
            onClick={this.zoomIn}
            disabled={!receiptUrl || isPdf}
            title={i18n.t('zoom_in') || 'Zoom in'}
          >
            <ZoomIn size={16} />
          </button>
          <button
            type="button"
            onClick={this.zoomOut}
            disabled={!receiptUrl || isPdf}
            title={i18n.t('zoom_out') || 'Zoom out'}
          >
            <ZoomOut size={16} />
          </button>
          <button
            type="button"
            onClick={this.rotateReceipt}
            disabled={!receiptUrl || isPdf}
            title={i18n.t('rotate') || 'Rotate'}
          >
            <RotateCw size={16} />
          </button>
          <button
            type="button"
            onClick={this.downloadReceipt}
            disabled={!receiptUrl}
            title={i18n.t('download') || 'Download'}
          >
            <Download size={16} />
          </button>
          <button
            type="button"
            onClick={this.toggleFullscreenReceipt}
            disabled={!receiptUrl}
            title={i18n.t('fullscreen') || 'Fullscreen'}
          >
            <Maximize2 size={16} />
          </button>
        </div>

        {this.state.showFullscreenReceipt && receiptUrl && (
          <div className="dm-receipt-fullscreen" onClick={this.toggleFullscreenReceipt}>
            <button className="dm-receipt-fullscreen__close" onClick={this.toggleFullscreenReceipt}>
              <X size={20} />
            </button>
            {isPdf ? (
              <iframe title="receipt" src={receiptUrl} className="dm-receipt-fullscreen__frame" />
            ) : (
              <img
                src={receiptUrl}
                alt="Receipt full"
                onClick={(e) => e.stopPropagation()}
                style={{ transform: `rotate(${receiptRotation}deg)` }}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  renderDocPreview(key, fileName) {
    const { zoom, rotation, fullscreen } = this.getDocView(key);
    const url = this.docUrl(fileName);
    const isPdf = /\.pdf$/i.test(fileName || '');

    return (
      <div className="dm-receipt-box">
        <div className="dm-receipt-canvas">
          {!url ? (
            <div className="dm-receipt-placeholder">
              {i18n.t('document_preview') || 'Document Preview'}
            </div>
          ) : isPdf ? (
            <div className="dm-receipt-pdf">
              <FileText size={32} />
              <span>{fileName}</span>
            </div>
          ) : (
            <img
              src={url}
              alt={fileName}
              style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }}
            />
          )}
        </div>

        <div className="dm-receipt-controls">
          <button
            type="button"
            onClick={() => this.docZoomIn(key)}
            disabled={!url || isPdf}
            title={i18n.t('zoom_in') || 'Zoom in'}
          >
            <ZoomIn size={16} />
          </button>
          <button
            type="button"
            onClick={() => this.docZoomOut(key)}
            disabled={!url || isPdf}
            title={i18n.t('zoom_out') || 'Zoom out'}
          >
            <ZoomOut size={16} />
          </button>
          <button
            type="button"
            onClick={() => this.docRotate(key)}
            disabled={!url || isPdf}
            title={i18n.t('rotate') || 'Rotate'}
          >
            <RotateCw size={16} />
          </button>
          <button
            type="button"
            onClick={() => this.downloadDoc(fileName)}
            disabled={!url}
            title={i18n.t('download') || 'Download'}
          >
            <Download size={16} />
          </button>
          <button
            type="button"
            onClick={() => this.docToggleFullscreen(key)}
            disabled={!url}
            title={i18n.t('fullscreen') || 'Fullscreen'}
          >
            <Maximize2 size={16} />
          </button>
        </div>

        {fullscreen && url && (
          <div className="dm-receipt-fullscreen" onClick={() => this.docToggleFullscreen(key)}>
            <button
              className="dm-receipt-fullscreen__close"
              onClick={() => this.docToggleFullscreen(key)}
            >
              <X size={20} />
            </button>
            {isPdf ? (
              <iframe title={key} src={url} className="dm-receipt-fullscreen__frame" />
            ) : (
              <img
                src={url}
                alt={fileName}
                onClick={(e) => e.stopPropagation()}
                style={{ transform: `rotate(${rotation}deg)` }}
              />
            )}
          </div>
        )}
      </div>
    );
  }
  renderDocuments() {
    const t = this.props.selectedTransaction;
    const enrollmentDoc = t?.client_approval_doc;
    const refundDoc = t?.refund_client_doc;

    if (!enrollmentDoc && !refundDoc) return null;

    return (
      <>
        {enrollmentDoc && (
          <section className="dm-section">
            <h6 className="dm-section-title">
              <FileText size={16} strokeWidth={1.75} />{' '}
              {i18n.t('enrollment_approval_document') || 'Enrollment Approval Document'}
            </h6>
            {this.renderDocPreview('enrollment_doc', enrollmentDoc)}
          </section>
        )}

        {refundDoc && (
          <section className="dm-section">
            <h6 className="dm-section-title">
              <FileText size={16} strokeWidth={1.75} />{' '}
              {i18n.t('refund_approval_document') || 'Refund Approval Document'}
            </h6>
            {this.renderDocPreview('refund_doc', refundDoc)}
          </section>
        )}
      </>
    );
  }
  renderClientApprovalDocSection() {
    const { selectedTransaction } = this.props;
    if (!selectedTransaction?.client_approval_doc) return null;

    return (
      <section className="dm-section">
        <h6 className="dm-section-title">
          <FileText size={16} strokeWidth={1.75} />{' '}
          {i18n.t('client_approval_document') || 'Client Approval Document'}
        </h6>
        {this.renderApprovalDocPreview()}
      </section>
    );
  }
  renderStrategyToggle() {
    const { strategyEnabled, submitting } = this.state;

    return (
      <section className="dm-section">
        <h6 className="dm-section-title">
          <Settings2 size={16} strokeWidth={1.75} /> {i18n.t('strategy') || 'Strategy'}
        </h6>
        <div className="dm-strategy-row">
          <div className="dm-strategy-row__info">
            <span className="dm-strategy-row__label">
              {i18n.t('enable_strategy') || 'Enable Strategy Mode'}
            </span>
            <span className="dm-strategy-row__hint">
              {i18n.t('strategy_hint') ||
                'When enabled, no guarantee amount hold will be placed on wallet approval.'}
            </span>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={strategyEnabled}
            className={`dm-switch ${strategyEnabled ? 'dm-switch--on' : ''}`}
            onClick={this.handleStrategyToggle}
            disabled={submitting}
          >
            <span className="dm-switch__thumb" />
          </button>
        </div>
      </section>
    );
  }

  renderTimeline() {
    const entries = this.buildTimeline();

    return (
      <section className="dm-section">
        <h6 className="dm-section-title">
          <Clock size={16} strokeWidth={1.75} />{' '}
          {i18n.t('activity_timeline') || 'Activity Timeline'}
        </h6>
        <div className="dm-timeline">
          {entries.map((entry) => (
            <div key={entry.key} className={`dm-timeline-item dm-timeline-item--${entry.tone}`}>
              <div className="dm-timeline-avatar">{this.getInitials(entry.actor)}</div>
              <div className="dm-timeline-content">
                <div className="dm-timeline-heading">
                  <strong>{entry.title}</strong>
                  <span className="dm-timeline-date">
                    {formatDate(entry.date)} · {entry.actor}
                  </span>
                </div>
                <p>{entry.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  renderApprovalUpload() {
    const { approvalDoc, approvalDocPreviewUrl, dragOver, fileError, submitting } = this.state;
    const isNewFile = approvalDoc instanceof File;

    return (
      <section className="dm-section" ref={this.approveDocRef}>
        <h6 className="dm-section-title">
          <Upload size={16} strokeWidth={1.75} />
          {i18n.t('approval_document') || 'Approval Document'}{' '}
          <span className="dm-required">*</span>
        </h6>

        <input
          type="file"
          id="dm-approval-doc"
          style={{ display: 'none' }}
          accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
          disabled={submitting}
          onChange={this.handleFileInputChange}
        />

        {!approvalDoc ? (
          <div
            className={`wam-upload-zone ${dragOver ? 'wam-upload-zone--dragover' : ''} ${
              fileError ? 'wam-upload-zone--error' : ''
            } ${submitting ? 'wam-upload-zone--disabled' : ''}`}
            onDragOver={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
          >
            <div className="wam-upload-zone__icon">
              <Upload size={24} />
            </div>
            <p className="wam-upload-zone__title">{i18n.t('drop_file_here') || 'Drop file here'}</p>
            <p className="wam-upload-zone__sub">JPG, PNG, PDF or Word (.doc, .docx) — max 5 MB</p>
            <button
              type="button"
              className="wam-upload-zone__btn"
              disabled={submitting}
              onClick={() => document.getElementById('dm-approval-doc').click()}
            >
              {i18n.t('browse_file') || 'Browse File'}
            </button>
          </div>
        ) : (
          <div className="wam-receipt-preview">
            <div className="wam-receipt-preview__header">
              <FileText size={15} className="wam-receipt-preview__file-icon" />
              <span className="wam-receipt-preview__name">{approvalDoc.name}</span>
              <button
                type="button"
                className="wam-receipt-preview__remove"
                onClick={this.removeApprovalDoc}
                disabled={submitting}
              >
                <X size={12} /> {i18n.t('remove') || 'Remove'}
              </button>
            </div>
            {approvalDocPreviewUrl && isNewFile && (
              <div className="wam-receipt-preview__body wam-receipt-preview__body--image">
                <img
                  src={approvalDocPreviewUrl}
                  alt="preview"
                  className="wam-receipt-preview__img"
                />
              </div>
            )}
          </div>
        )}

        {fileError && (
          <div className="wam-upload-error">
            <AlertCircle size={13} /> {fileError}
          </div>
        )}
      </section>
    );
  }

  renderDeductionForm() {
    const { deductionType, deductionCustomReason, deductionError, submitting } = this.state;
    const isCustom = deductionType === DEDUCTION_CUSTOM;

    const user = this.props.selectedTransaction?.user;
    const email = user?.email;
    const bidderName = user?.name || i18n.t('bidder') || 'Bidder';
    const reasonText = this.getDeductionReasonText();

    return (
      <section className="dm-section">
        <h6 className="dm-section-title">
          <MinusCircle size={16} strokeWidth={1.75} />
          {i18n.t('deduction_reason') || 'Deduction Reason'} <span className="dm-required">*</span>
        </h6>

        <div className="dm-reason-list" role="radiogroup">
          {DEDUCTION_REASONS.map((r) => (
            <label
              key={r.key}
              className={`dm-reason ${deductionType === r.key ? 'dm-reason--active' : ''} ${
                submitting ? 'dm-reason--disabled' : ''
              }`}
            >
              <input
                type="radio"
                name="dm-deduction-reason"
                value={r.key}
                checked={deductionType === r.key}
                onChange={this.handleDeductionTypeChange}
                disabled={submitting}
              />
              <span className="dm-reason__radio" />
              <span className="dm-reason__label">{i18n.t(r.i18nKey) || r.label}</span>
            </label>
          ))}
        </div>

        {isCustom && (
          <textarea
            ref={this.deductReasonRef}
            className={`dm-textarea dm-reason__custom ${
              deductionError && !deductionCustomReason.trim() ? 'dm-textarea--error' : ''
            }`}
            value={deductionCustomReason}
            onChange={(e) =>
              this.setState({ deductionCustomReason: e.target.value, deductionError: '' })
            }
            placeholder={i18n.t('enter_deduction_reason')}
            rows={3}
            disabled={submitting}
          />
        )}

        {deductionError && <span className="dm-field-error">{deductionError}</span>}

        {/* ── Notification notice ───────────────────────── */}
        <div className={`dm-notify ${email ? '' : 'dm-notify--warn'}`} role="note">
          <div className="dm-notify__icon">
            {email ? <Mail size={16} /> : <AlertCircle size={16} />}
          </div>

          <div className="dm-notify__body">
            <p className="dm-notify__title">
              {email
                ? i18n.t('penalty_notification_title') || 'Bidder will be notified'
                : i18n.t('penalty_notification_no_email') || 'No email on file for this bidder'}
            </p>

            {email ? (
              <p className="dm-notify__text">
                {i18n.t('penalty_notification_text_1') ||
                  'An email notification about this penalty and its reason will be sent to'}{' '}
                <span className="dm-notify__recipient">
                  {bidderName} · <strong className="dm-notify__email">{email}</strong>
                </span>
              </p>
            ) : (
              <p className="dm-notify__text">
                {i18n.t('penalty_notification_no_email_text') ||
                  'The deduction will be applied, but the bidder may not receive an email notification.'}
              </p>
            )}

            <div className="dm-notify__preview">
              <span className="dm-notify__preview-label">
                {i18n.t('reason_to_be_sent') || 'Reason to be sent'}
              </span>
              <p className={`dm-notify__preview-text ${reasonText ? '' : 'is-empty'}`}>
                {reasonText ||
                  i18n.t('select_reason_to_preview') ||
                  'Select or enter a reason to preview it here'}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  render() {
    const { show, selectedTransaction } = this.props;
    const { showRejectInput, showDeductInput, rejectReason, rejectError, submitting } = this.state;
    const deductReady = Boolean(this.getDeductionReasonText());

    if (!selectedTransaction) return null;

    const bidderName = selectedTransaction.user?.name || '--';
    const orgName = selectedTransaction.group?.organization_details?.organization_name || '--';

    const header = (
      <div className="dm-header">
        <div>
          <h2 className="dm-header-title">{bidderName}</h2>
          <p className="dm-header-subtitle">
            {selectedTransaction.enroll_number ? `ENR-${selectedTransaction.enroll_number}` : '--'}{' '}
            · {orgName}
          </p>
        </div>
        <StatusNewBadge status={ENROLLMENT_REFUND_STATUS[selectedTransaction.refund_status]} />
      </div>
    );

    return (
      <CustomModal isOpen={show} header={header} onClose={this.handleClose}>
        <div className="dm-body">
          {this.renderBidderDetails()}
          {this.renderAuctionAccess()}
          {this.renderPaymentVerification()}
          {this.renderDocuments()}

          {this.renderTimeline()}

          {!showRejectInput &&
            !showDeductInput &&
            this.requireAttachment &&
            this.renderApprovalUpload()}

          {showDeductInput && this.canDeduct && this.renderDeductionForm()}

          {showRejectInput && (
            <section className="dm-section">
              <label htmlFor="dm-reject-reason" className="dm-section-title">
                {i18n.t('reason_for_rejection') || 'Reason for Rejection'}{' '}
                <span className="dm-required">*</span>
              </label>
              <textarea
                id="dm-reject-reason"
                ref={this.rejectReasonRef}
                className={`dm-textarea ${rejectError ? 'dm-textarea--error' : ''}`}
                value={rejectReason}
                onChange={(e) => this.setState({ rejectReason: e.target.value, rejectError: '' })}
                placeholder={
                  i18n.t('enter_reject_reason') ||
                  'Enter the reason for rejecting this enrollment...'
                }
                rows={4}
                disabled={submitting}
              />
              {rejectError && <span className="dm-field-error">{rejectError}</span>}
            </section>
          )}
        </div>

        <div className="dm-footer">
          {showRejectInput ? (
            <>
              <button
                type="button"
                className="dm-btn dm-btn--outline"
                onClick={this.handleBackFromReject}
                disabled={submitting}
              >
                <ArrowLeft size={18} /> {i18n.t('back') || 'Back'}
              </button>
              <button
                type="button"
                className="dm-btn dm-btn--danger"
                onClick={this.handleReject}
                disabled={submitting || !rejectReason.trim()}
              >
                <XCircle size={18} />{' '}
                {submitting
                  ? i18n.t('rejecting') || 'Rejecting...'
                  : i18n.t('confirm_reject') || 'Confirm Reject'}
              </button>
            </>
          ) : showDeductInput ? (
            <>
              <button
                type="button"
                className="dm-btn dm-btn--outline"
                onClick={this.handleBackFromDeduct}
                disabled={submitting}
              >
                <ArrowLeft size={18} /> {i18n.t('back')}
              </button>
              <button
                type="button"
                className="dm-btn dm-btn--warning"
                onClick={this.handleDeduct}
                disabled={submitting || !deductReady}
              >
                <MinusCircle size={18} />{' '}
                {submitting ? i18n.t('applying') : i18n.t('confirm_deduction')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="dm-btn dm-btn--icon"
                title={i18n.t('help') || 'Help'}
              >
                <HelpCircle size={18} />
              </button>

              {this.canAct ? (
                <>
                  <button
                    type="button"
                    className="dm-btn dm-btn--outline-danger"
                    onClick={this.handleRejectClick}
                    disabled={submitting}
                  >
                    <XCircle size={18} /> {i18n.t('reject') || 'Reject'}
                  </button>
                  {this.canDeduct && (
                    <button
                      type="button"
                      className="dm-btn dm-btn--outline-warning"
                      onClick={this.handleDeductClick}
                      disabled={submitting}
                    >
                      <MinusCircle size={18} /> {i18n.t('deduction')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="dm-btn dm-btn--success"
                    onClick={this.handleApprove}
                    disabled={submitting}
                  >
                    <CheckCircle size={18} />{' '}
                    {submitting
                      ? i18n.t('approving') || 'Approving...'
                      : i18n.t('approve') || 'Approve'}
                  </button>
                </>
              ) : (
                <button type="button" className="dm-btn dm-btn--outline" onClick={this.handleClose}>
                  {i18n.t('close') || 'Close'}
                </button>
              )}
            </>
          )}
        </div>
      </CustomModal>
    );
  }
}
